import { apiHelper } from '@/utils'
import { API } from '@/constants'
import { buildListingParams } from './listing.params'

/**
 * User management.
 *
 * Distinct from inbox.service's agent calls, which drive the agent desk's own
 * team screen (`/v1/agents`, gated on users.is_admin, password handed over in
 * person). These are the admin screens: `/v1/users`, gated on spatie
 * permissions, and a password is never sent — the server mails a first-time
 * link and `invite_sent` says whether that went out.
 */
export const usersService = {
  getAllUsers,
  getUser,
  createUser,
  updateUser,
  updateUserStatus,
  updateUserLoginAccess,
  unlockUser,
  getProfile,
  updateProfile,
}

const API_URL = API.BASE_URL + '/v1'

function buildParams(filter) {
  return buildListingParams(filter, {
    // Arrays: every one of these is a multi-select on the page. An untouched
    // one is dropped rather than sent empty.
    is_active: filter?.isActive,
    is_locked: filter?.isLocked,
    is_admin: filter?.isAdmin,
    // Numeric brand ids, plus the literal 'platform' for the staff seats whose
    // tenant_id is NULL — `tenant_id[]=null` would match nothing.
    tenant_id: filter?.tenantIds,
    role_uuids: filter?.roleUuids,
  })
}

export async function getAllUsers(filter) {
  return apiHelper(API_URL).get('/users', { params: buildParams(filter) })
}

export async function getUser(uuid) {
  return apiHelper(API_URL).get(`/users/${uuid}`)
}

export async function createUser(data) {
  return apiHelper(API_URL).post('/users', body(data))
}

export async function updateUser(uuid, data) {
  return apiHelper(API_URL).put(`/users/${uuid}`, body(data))
}

export async function updateUserStatus(uuid, isActive) {
  return apiHelper(API_URL).patch(`/users/${uuid}/status`, { is_active: isActive })
}

export async function updateUserLoginAccess(uuid, isAllowLogin) {
  return apiHelper(API_URL).patch(`/users/${uuid}/login-access`, { is_allow_login: isAllowLogin })
}

export async function unlockUser(uuid) {
  return apiHelper(API_URL).patch(`/users/${uuid}/unlock`)
}

export async function getProfile() {
  return apiHelper(API_URL).get('/users/profile')
}

export async function updateProfile(data) {
  return apiHelper(API_URL).put('/users/profile', {
    name: data.name,
    email: data.email,
    // Omitted entirely when blank: `password: ''` would fail the server's
    // `between:10,255` rule on a form where the user only changed their name.
    ...(data.password
      ? {
          password: data.password,
          password_confirmation: data.passwordConfirmation,
          current_password: data.currentPassword,
        }
      : {}),
  })
}

/**
 * The create/update body.
 *
 * `tenant_ids` is only sent when the form carries the field — a supervisor's
 * form does not, and the server takes the brands from them in that case.
 * Sending an empty list would read as "make this a platform seat", which a
 * supervisor may not do and the server trims back to their own brands.
 *
 * A LIST, always: an agent can work on more than one brand, and a field that is
 * sometimes a number and sometimes an array is a field every caller gets wrong
 * once.
 */
function body(data) {
  const payload = {
    name: data.name,
    email: data.email,
    role_uuid: data.roleUuid || null,
    is_admin: !!data.isAdmin,
  }

  // Only when the operator typed one. On the edit form an empty box means
  // "leave their password alone", so sending '' would read as a reset to
  // nothing and be refused by the length rule.
  if (data.password) payload.password = data.password

  if (data.tenantIds !== undefined) {
    payload.tenant_ids = (data.tenantIds || []).map(Number)
  }

  if (data.isAllowLogin !== undefined) {
    payload.is_allow_login = !!data.isAllowLogin
  }

  return payload
}
